import { useCallback, useEffect, useState } from "react";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import axiosInstance, { clearAuthStorage } from "../../hooks/axios";
import { formatUkDateTime } from "../../utils/dateUtils";

type SessionRow = {
  id: string;
  deviceLabel: string;
  userAgent: string | null;
  ipAddress: string | null;
  lastActiveAt: string;
  expiresAt: string;
  createdAt: string;
  isCurrent: boolean;
};

function formatWhen(iso: string): string {
  return formatUkDateTime(iso);
}

export default function ManageSessions() {
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get<SessionRow[]>("/auth/sessions");
      setSessions(res.data);
    } catch {
      setMessage("Failed to load sessions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleRevoke = async (id: string, isCurrent: boolean) => {
    if (
      !window.confirm(
        isCurrent
          ? "Sign out this device? You will be redirected to login."
          : "Revoke this session?",
      )
    ) {
      return;
    }
    try {
      const res = await axiosInstance.delete(`/auth/sessions/${id}`);
      if (res.data?.wasCurrent || isCurrent) {
        clearAuthStorage();
        window.location.href = "/signin";
        return;
      }
      setMessage("Session revoked");
      await load();
    } catch {
      setMessage("Failed to revoke session");
    }
  };

  const handleRevokeOthers = async () => {
    if (!window.confirm("Sign out all other devices?")) return;
    try {
      await axiosInstance.post("/auth/sessions/revoke-others");
      setMessage("Other sessions signed out");
      await load();
    } catch {
      setMessage("Failed to revoke other sessions");
    }
  };

  const otherCount = sessions.filter((s) => !s.isCurrent).length;

  return (
    <div className="space-y-6 max-w-4xl">
      <PageBreadcrumb pageTitle="Manage Sessions" />

      <p className="mt-4 text-sm text-gray-600 max-w-3xl">
        View devices where you are signed in to the admin dashboard. Revoke any session you
        do not recognise. Sessions expire automatically based on your JWT settings (typically
        one hour).
      </p>

      <div className="mt-6 flex flex-wrap gap-3">
        {otherCount > 0 && (
          <button
            type="button"
            onClick={handleRevokeOthers}
            className="px-4 py-2 border border-red-300 text-red-700 rounded-lg text-sm font-medium hover:bg-red-50"
          >
            Sign out all other devices ({otherCount})
          </button>
        )}
        <button
          type="button"
          onClick={load}
          className="px-4 py-2 border rounded-lg text-sm text-gray-700 hover:bg-gray-50"
        >
          Refresh
        </button>
      </div>

      {message && (
        <p
          className={`mt-4 text-sm ${
            message.includes("Failed") ? "text-red-600" : "text-green-600"
          }`}
        >
          {message}
        </p>
      )}

      <div className="mt-6 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03]">
        {loading ? (
          <p className="px-6 py-8 text-gray-500">Loading...</p>
        ) : sessions.length === 0 ? (
          <p className="px-6 py-8 text-gray-500">
            No active sessions. Sign in again to create a session record.
          </p>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Device</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">IP</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">
                  Last active
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Expires</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">Action</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((row) => (
                <tr key={row.id} className="border-b hover:bg-gray-50/80">
                  <td className="px-4 py-3">
                    <span className="font-medium text-gray-800">{row.deviceLabel}</span>
                    {row.isCurrent && (
                      <span className="ml-2 text-xs font-medium text-green-600">
                        This device
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600 font-mono text-xs">
                    {row.ipAddress ?? "—"}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {formatWhen(row.lastActiveAt)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {formatWhen(row.expiresAt)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => handleRevoke(row.id, row.isCurrent)}
                      className="text-red-600 hover:underline font-medium"
                    >
                      {row.isCurrent ? "Sign out" : "Revoke"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
